import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion, type Transition } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { log, now } from '../lib/devLog';

type Props = {
  routeKey: string;
  children: React.ReactNode;
};

export default function RouteTransition({ routeKey, children }: Props) {
  const location = useLocation();
  const renderIdRef = useRef(0);
  const firstRenderRef = useRef(true);
  renderIdRef.current += 1;
  const reduce = useReducedMotion();
  const initial = reduce || firstRenderRef.current
    ? { opacity: 1, y: 0, scale: 1 }
    : { opacity: 1, y: 8, scale: 0.99 };
  const animate = reduce ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 };
  const exit = reduce ? { opacity: 1 } : { opacity: 1, y: -4, scale: 0.995 };
  const transition: Transition = reduce ? { duration: 0 } : { duration: 0.45, ease: [0.16, 1, 0.3, 1] };

  useEffect(() => {
    log('[ui] RouteTransition mount', {
      renderId: renderIdRef.current,
      pathname: location.pathname,
      routeKey,
      ts: now(),
    });
    firstRenderRef.current = false;
    return () => {
      log('[ui] RouteTransition unmount', {
        renderId: renderIdRef.current,
        pathname: location.pathname,
        routeKey,
        ts: now(),
      });
    };
  }, [location.pathname, routeKey]);

  return (
    <AnimatePresence initial={false} mode="wait">
      <motion.div
        key={routeKey}
        initial={initial}
        animate={animate}
        exit={exit}
        transition={transition}
        style={{ minHeight: '100%' }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}
